// src/host/MovePlannerHostProvider.tsx
import React from "react";
import { jsx } from "react/jsx-runtime";
var MovePlannerHostContext = React.createContext(null);
var MovePlannerHostProvider = ({ value, children }) => /* @__PURE__ */ jsx(MovePlannerHostContext.Provider, { value, children });
function useMovePlannerHost() {
  const host = React.useContext(MovePlannerHostContext);
  if (!host) throw new Error("useMovePlannerHost must be used inside a MovePlannerHostProvider.");
  return host;
}

// src/host/defaults.ts
var DEALER_CAPABILITIES = Object.freeze({
  roCapture: true,
  dynamicWorkflowSteps: true,
  confirmationContacts: true,
  contactsFromSourceMove: true,
  contactsAutofill: true,
  rideshareForReturn: true,
  enforcementMode: true,
  overlapEnforcement: true,
  driverRows: true,
  suggestedTimes: true,
  serviceLocations: true,
  lanePairReuse: true,
  laneMap: true,
  eventlogAudit: true,
  vinDecoder: true,
  programCoverage: true,
  appointmentMode: true,
  scheduledFromPlanningContext: true,
  liveRegionGate: true,
  timeValidityCheck: true,
  loanerSwapOnTypeChange: true,
  productChoiceSwitch: false,
  workflowsetResetToFirstListed: false,
  defaultWorkflowsetWithSourceMove: true,
  programAdminsMayPlan: true,
  separateTimeSection: true,
  swapHiddenForValet: true,
  notesLabelOptional: false,
  emailCopy: "internal-team",
  laneFailureCopy: "route-box",
  laneEndpointsFromLocations: false,
  googleSuggestionsCopy: true,
  optionalVehicleBlurb: false,
  loanerNoticeAsksForDetails: false,
  referenceIdFromLookup: false,
  priceNoteFullWidth: true
});
function resolveCapabilities(overrides = {}) {
  return { ...DEALER_CAPABILITIES, ...overrides };
}

// src/provider/plannerContext.ts
import React2 from "react";
var MovePlannerContext = React2.createContext(null);
function useMovePlanner() {
  const planner = React2.useContext(MovePlannerContext);
  if (!planner) throw new Error("useMovePlanner must be used under a Move Planner provider.");
  return planner;
}

// src/forms/type/offeredTypes.ts
import { resolveMoveOffering } from "@hopdrive/sdk-moves";
function moveOfferingFor(facts) {
  if (!facts.known || !facts.canCreate) return null;
  return resolveMoveOffering({ ownedProductKeys: facts.heldValetKeys, canCreate: facts.canCreate });
}
function offeredMoveTypes(types, offering, allowList) {
  const offered = types.filter((t) => offering[t.value]?.offered);
  if (offered.length === 0) return { types: offered, narrowedBy: "offering" };
  if (!Array.isArray(allowList)) return { types: offered, narrowedBy: null };
  const allowed = offered.filter((t) => allowList.includes(t.value));
  return { types: allowed, narrowedBy: allowed.length === 0 ? "list" : null };
}
function typeAllowList(rooftopConfig, isAppointment) {
  return isAppointment ? void 0 : rooftopConfig?.allowed_move_types;
}
function isRooftopConfigLoaded(configRooftopId, rooftopId) {
  return !!rooftopId && configRooftopId === rooftopId;
}
function appliedConfigRooftopId(rooftops, rooftopId) {
  return rooftops?.length && rooftopId ? rooftopId : null;
}
function offeredTypesOnceLoaded(types, offering, rooftopConfig) {
  if (!offering || !rooftopConfig.loaded) return null;
  return offeredMoveTypes(types, offering, rooftopConfig.allowList);
}

export {
  MovePlannerHostProvider,
  useMovePlannerHost,
  DEALER_CAPABILITIES,
  resolveCapabilities,
  moveOfferingFor,
  offeredMoveTypes,
  typeAllowList,
  isRooftopConfigLoaded,
  appliedConfigRooftopId,
  offeredTypesOnceLoaded,
  MovePlannerContext,
  useMovePlanner
};
//# sourceMappingURL=chunk-HLQMKPF5.mjs.map